import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from '../models/User';

export interface EmailOptions {
  subject: string;
  body: string;
  template?: string;
  data?: Record<string, any>;
}

/**
 * Sends an email to a specific user
 * @param user The user to send the email to
 * @param options Email options including subject and body
 * @param senderId The numerical ID of the user sending the email (from users table)
 * @returns Promise<void>
 */
export const sendEmailToUser = async (user: User, options: EmailOptions, senderId: string): Promise<void> => {
  if (!user.email) {
    throw new Error('User does not have an email address');
  }

  const functions = getFunctions();
  const sendEmail = httpsCallable(functions, 'sendEmail');

  try {
    await sendEmail({
      userId: senderId, // Pass the numerical user ID from users table
      to: user.email,
      subject: options.subject,
      body: options.body,
      template: options.template,
      data: options.data
    });
  } catch (error) {
    console.error('Failed to send email:', error);
    throw error;
  }
};

/**
 * Sends an email to multiple users
 * @param users Array of users to send the email to
 * @param options Email options including subject and body
 * @param senderId The numerical ID of the user sending the email (from users table)
 * @returns Promise<void>
 */
export const sendEmailToUsers = async (users: User[], options: EmailOptions, senderId: string): Promise<void> => {
  const functions = getFunctions();
  const sendBulkEmail = httpsCallable(functions, 'sendBulkEmail');

  const validUsers = users.filter(user => user.email);
  if (validUsers.length === 0) {
    throw new Error('No valid email addresses found');
  }

  try {
    await sendBulkEmail({
      userId: senderId, // Pass the numerical user ID from users table
      to: validUsers.map(user => user.email),
      subject: options.subject,
      body: options.body,
      template: options.template,
      data: options.data
    });
  } catch (error) {
    console.error('Failed to send bulk email:', error);
    throw error;
  }
};

/**
 * Sends a notification email to a user
 * @param user The user to send the notification to
 * @param title The notification title
 * @param message The notification message
 * @param actionUrl Optional URL for the notification action
 * @param senderId The numerical ID of the user sending the email (from users table)
 * @returns Promise<void>
 */
export const sendNotificationEmail = async (
  user: User, 
  title: string, 
  message: string, 
  actionUrl?: string,
  senderId?: string
): Promise<void> => {
  const functions = getFunctions();
  const sendNotification = httpsCallable(functions, 'sendNotification');

  const emailBody = `
    <h2>${title}</h2>
    <p>${message}</p>
    ${actionUrl ? `<p><a href="${actionUrl}" style="background-color: #007bff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Details</a></p>` : ''}
  `;

  try {
    await sendNotification({
      userId: user.id,
      type: 'message_received', // Use MESSAGE_RECEIVED type for all notification emails
      title: title,
      message: message,
      emailContent: emailBody
    });
  } catch (error) {
    console.error('Failed to send notification email:', error);
    throw error;
  }
}; 